import { useEffect, useMemo, useState } from 'react';
import { ACTIVE_PROJECT_KEY, getActiveProject, setActiveProject } from '../../utils/projectStorage';

const ProjectSwitcher = ({ projects = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeProject, setActiveProjectState] = useState(() => getActiveProject());

  useEffect(() => {
    setActiveProjectState(getActiveProject());
  }, []);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === ACTIVE_PROJECT_KEY) {
        setActiveProjectState(getActiveProject());
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', handleStorage);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', handleStorage);
      }
    };
  }, []);

  const availableProjects = useMemo(() => {
    if (projects.length > 0) {
      return projects.map((project) => ({
        id: project.id || project._id || project.slug || project.name,
        name: project.name || project.title || 'Untitled project',
      }));
    }

    return [];
  }, [projects]);

  const handleSelectProject = (project) => {
    setActiveProject(project);
    setActiveProjectState(project);
    setIsOpen(false);
  };

  const handleManualSelection = () => {
    if (typeof window === 'undefined') return;

    const name = window.prompt('Set current project name');

    if (!name) return;

    const project = {
      id: `custom-${Date.now()}`,
      name: name.trim(),
    };

    handleSelectProject(project);
  };

  const displayName = activeProject?.name || 'Select project';

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-primary/50 hover:text-primary"
      >
        <span className="inline-flex h-2 w-2 rounded-full bg-green-500" aria-hidden />
        <span>{displayName}</span>
        <svg className="h-4 w-4 text-slate-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.08 1.04l-4.25 4.25a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" />
        </svg>
      </button>

      {isOpen ? (
        <div className="absolute right-0 z-40 mt-2 w-64 rounded-md border border-slate-200 bg-white shadow-lg">
          <div className="max-h-64 overflow-y-auto py-2">
            {availableProjects.length > 0 ? (
              availableProjects.map((project) => (
                <button
                  key={project.id}
                  type="button"
                  onClick={() => handleSelectProject(project)}
                  className="flex w-full items-start px-4 py-2 text-left text-sm text-slate-600 transition hover:bg-slate-50"
                >
                  <div className="flex flex-col">
                    <span className="font-medium text-slate-800">{project.name}</span>
                    {project.id ? <span className="text-xs text-slate-400">{project.id}</span> : null}
                  </div>
                </button>
              ))
            ) : (
              <div className="px-4 py-2 text-sm text-slate-500">
                No project list provided yet. Use “Set project manually” to define the current context.
              </div>
            )}
          </div>
          <div className="border-t border-slate-100 p-2">
            <button
              type="button"
              onClick={handleManualSelection}
              className="w-full rounded-md border border-dashed border-primary/40 px-3 py-2 text-sm font-medium text-primary transition hover:border-primary hover:bg-primary/5"
            >
              Set project manually
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ProjectSwitcher;



